import * as Sentry from "@sentry/react";

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const isProduction = import.meta.env.MODE === "production";

/**
 * Noise shared between error events (`ignoreErrors`) and forwarded console
 * logs (`beforeSendLog`) so the two filters can't drift out of sync.
 */
const NOISE_PATTERNS = [
  // Network errors
  "Failed to fetch",
  "NetworkError",
  "Network request failed",
  "Load failed",
  "ChunkLoadError",

  // Browser quirks
  "ResizeObserver loop",
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",

  // User-initiated
  "AbortError",
  "The operation was aborted",
  "The user aborted a request",

  // Service worker
  "ServiceWorker",
  "Failed to register a ServiceWorker",
  "The Service Worker system has shutdown",
];

/**
 * Whether a message matches known noise (network blips, browser quirks,
 * expected SW/abort conditions) that shouldn't be forwarded as a Sentry log.
 */
export const isNoiseMessage = (message: string): boolean =>
  message !== "" &&
  NOISE_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase())
  );

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn("⚠️ Sentry DSN not configured");
    return;
  }

  // Never send events from localhost, regardless of build mode — local dev
  // should never reach Sentry.
  if (isLocalhost) {
    console.log("🔇 Sentry disabled on localhost");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_DEPLOY_ENV || import.meta.env.MODE,
    release: `golf-on-ourse@${import.meta.env.VITE_APP_VERSION}`,

    enableLogs: true,

    // Performance monitoring
    tracesSampleRate: isProduction ? 0.2 : 1.0,

    // Session replay - only on errors in production
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0,

    // Don't send PII by default
    sendDefaultPii: false,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Forward console.warn/error to Sentry logs. console.log is excluded
      // to keep ingest volume low on a 24/7 kiosk.
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],

    // Filter out noise
    ignoreErrors: NOISE_PATTERNS,

    // Drop forwarded console logs that match the same noise as ignoreErrors
    // (network blips, ResizeObserver, aborts, SW lifecycle chatter).
    beforeSendLog(log) {
      if (isNoiseMessage(String(log.message))) {
        return null;
      }
      return log;
    },
  });

  // Set context tags
  Sentry.setTags({
    "app.version": import.meta.env.VITE_APP_VERSION,
    "app.build_id": import.meta.env.VITE_BUILD_ID,
    "app.git_sha": import.meta.env.VITE_GIT_SHA,
  });

  console.log("✅ Sentry initialized", {
    environment: import.meta.env.VITE_DEPLOY_ENV || import.meta.env.MODE,
    release: `golf-on-ourse@${import.meta.env.VITE_APP_VERSION}`,
  });
};

/**
 * Max length for free-form text (e.g. response bodies) attached to Sentry
 * context. Keeps event payloads small and avoids fragmenting error grouping
 * when the raw value varies per request.
 */
export const MAX_SENTRY_CONTEXT_TEXT_LENGTH = 500;

/**
 * Truncates a string for inclusion in Sentry context/extras. Use this for
 * any dynamic, potentially large text (response bodies, stack dumps, etc.)
 * instead of embedding it directly in an error message, which would
 * fragment Sentry grouping.
 */
export const truncateForSentry = (
  text: string,
  maxLength: number = MAX_SENTRY_CONTEXT_TEXT_LENGTH
): string => (text.length > maxLength ? `${text.slice(0, maxLength)}…` : text);

/**
 * Capture an exception with optional context
 */
export const captureException = (
  error: Error | unknown,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Set user context for Sentry
 */
export const setUser = (user: { id?: string; email?: string } | null) => {
  Sentry.setUser(user);
};

type DeviceContext = {
  id: string;
  name: string | null;
  course: { id: string; displayName: string | null } | null;
  hole: { holeNumber: number } | null;
};

/**
 * Tags every subsequent Sentry event (errors, replays, messages) and metric
 * gauge with kiosk device/course identity, so "is device X working" is a
 * Sentry search instead of unanswerable. setUser is what makes metrics
 * (Sentry.metrics.gauge) carry the device — the SDK auto-enriches every
 * metric with the current scope's user, but not with arbitrary tags.
 * Device id/name are set as both user and tags: tags drive issue
 * search/columns, but Sentry Logs only surface `user.*` attributes (not
 * tags), so course/hole are duplicated onto the user object too.
 */
export const setDeviceContext = (device: DeviceContext) => {
  Sentry.setUser({
    id: device.id,
    username: device.name ?? undefined,
    ...(device.course
      ? {
          courseId: device.course.id,
          courseName: device.course.displayName ?? undefined,
        }
      : {}),
    ...(device.hole ? { holeNumber: device.hole.holeNumber } : {}),
  });

  Sentry.setTags({
    "device.id": device.id,
    ...(device.name ? { "device.name": device.name } : {}),
    ...(device.course
      ? {
          "course.id": device.course.id,
          "course.name": device.course.displayName ?? undefined,
        }
      : {}),
    ...(device.hole ? { "hole.number": device.hole.holeNumber } : {}),
  });
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

let globalErrorHandlersRegistered = false;

/**
 * Route unhandled promise rejections and uncaught errors to Sentry with
 * kiosk-relevant context, in case they fire before Sentry's own
 * instrumentation attaches. Idempotent — repeated calls (e.g. HMR) are a no-op.
 */
export const registerGlobalErrorHandlers = () => {
  if (typeof window === "undefined" || globalErrorHandlersRegistered) {
    return;
  }
  globalErrorHandlersRegistered = true;

  const kioskContext = () => ({
    online: navigator.onLine,
    buildId: import.meta.env.VITE_BUILD_ID,
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureException(event.reason, {
      type: "unhandledrejection",
      ...kioskContext(),
    });
  });

  window.addEventListener("error", (event) => {
    captureException(event.error ?? event.message, {
      type: "error",
      ...kioskContext(),
    });
  });
};
